import { IEventSubscriber } from "./IEventSubscriber";
import { EventPublisher } from "./EventPublisher";
import { Dispatcher } from "./Dispatcher";
import { ObjectComparer } from "../Utils/ObjectComparer";
import * as $ from "jquery";
import { AdviserConfigurationHistoryClient } from "../../Wrap/Api/Adviser/AdviserConfigurationHistory";

export interface IStore {
    onChange(listener: IEventSubscriber, callback: () => void): void;

    removeChangeListener(listener: IEventSubscriber): void;

    isLoading(): boolean;
}

export abstract class BaseStore<TState> implements IStore, IEventSubscriber {
    private _state: TState;
    private _eventPublisher: EventPublisher;
    private _stateChangeTimeout: any;
    protected debugEnabled: boolean;

    public constructor() {
        this._eventPublisher = new EventPublisher();
        this._state = this.getDefaultState();
        this._stateChangeTimeout = null;
        this.debugEnabled = false;
    }

    public onChange(listener: IEventSubscriber, callback: () => void): void {
        this._eventPublisher.register(listener, callback);
    }

    public removeChangeListener(listener: IEventSubscriber): void {
        this._eventPublisher.unregister(listener);
    }

    public abstract isLoading(): boolean;

    protected subscribeToDispatcher<TEventPayload>(
        dispatcher: Dispatcher<TEventPayload>,
        onDispatch: (action: TEventPayload) => void
    ): void {
        dispatcher.register(this, (eventData) => {
            onDispatch(eventData);
            this.scheduleStateChangeAsync();
        });
    }

    /// Be wary of setState where you are trying to update an array. To replace an array first replace it
    /// by a null. To extend an array simply add the element to the newState. Replacing an array by an array
    /// will produce a merge, not replace as expected
    protected setState(newState: TState) {
        this.modifyState(newState);
    }

    protected getState(): TState {
        return this._state;
    }

    // since there is no elegant way to really reset the state using this.setState() due to jQuery.extend() shittyness
    protected resetState() {
        this._state = this.getDefaultState();
        this.debug("BaseStore: reset to default state", this._state);
        this.scheduleStateChangeAsync();
    }

    private scheduleStateChangeAsync() {
        if (this._stateChangeTimeout) {
            this.debug("BaseStore: onChange already scheduled");
            return;
        } else {
            this.debug("BaseStore: onChange scheduled");
            this._stateChangeTimeout = setTimeout(() => {
                this._stateChangeTimeout = null;
                this.emitStateChanged();
            }, 0);
        }
    }

    private modifyState(newState: TState): void {
        this.debug("BaseStore: State change requested", newState);
        const mergedState = $.extend(true, {}, this._state);
        Object.keys(newState).forEach((key) => {
            mergedState[key] = newState[key];
        });
        if (!this.statesEqual(this._state, mergedState)) {
            this.debug("BaseStore: State updated", mergedState);
            this._state = mergedState;
            this.scheduleStateChangeAsync();
        }
    }

    private emitStateChanged(): void {
        this.debug("BaseStore: onChange emitted", this._state);
        this._eventPublisher.dispatch();
    }

    protected statesEqual(currentState: TState, newState: TState): boolean {
        return ObjectComparer.isSame(currentState, newState);
    }

    protected abstract getDefaultState(): TState;

    protected debug(message: string, param?: any) {
        if (this.debugEnabled) {
            console.debug(message, param);
        }
    }
}
